import {
  useMemo,
  useState,
} from "react";

import type {
  Aluno,
  NovoAlunoInput,
} from "../types";

import "./AlunoModal.css";


type AlunoModalProps = {
  open: boolean;

  alunos: Aluno[];

  loading: boolean;

  saving: boolean;

  error: string;

  onClose: () => void;

  onAdicionarExistente: (
    aluno: Aluno,
  ) => Promise<void>;

  onCadastrarNovo: (
    input: NovoAlunoInput,
  ) => Promise<void>;
};


const LEVELS = [
  "Iniciante",
  "D",
  "C",
  "B",
  "A",
];


export default function AlunoModal({
  open,
  alunos,
  loading,
  saving,
  error,
  onClose,
  onAdicionarExistente,
  onCadastrarNovo,
}: AlunoModalProps) {
  const [
    mode,
    setMode,
  ] =
    useState<
      "existente" |
      "novo"
    >(
      "existente",
    );


  const [
    search,
    setSearch,
  ] =
    useState("");


  const [
    form,
    setForm,
  ] =
    useState<NovoAlunoInput>({
      nome: "",

      nivel:
        "Iniciante",

      telefone: "",

      nascimento: "",
    });


  const filteredAlunos =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();


        if (!term) {
          return alunos;
        }


        return alunos.filter(
          (aluno) =>
            aluno.nome
              .toLowerCase()
              .includes(
                term,
              ) ||
            (
              aluno.telefone ??
              ""
            )
              .toLowerCase()
              .includes(
                term,
              ) ||
            aluno.nivel
              .toLowerCase()
              .includes(
                term,
              ),
        );
      },
      [
        alunos,
        search,
      ],
    );


  if (!open) {
    return null;
  }


  async function handleCreate() {
    if (
      !form.nome.trim()
    ) {
      return;
    }


    await onCadastrarNovo({
      ...form,

      nome:
        form.nome.trim(),

      telefone:
        form.telefone.trim(),
    });
  }


  return (
    <div
      className="aluno-modal-backdrop"
      onClick={
        onClose
      }
    >
      <div
        className="aluno-modal"
        onClick={(
          event,
        ) =>
          event.stopPropagation()
        }
      >
        <div className="aluno-modal-header">
          <div>
            <span className="turmas-eyebrow">
              ALUNO
            </span>

            <h3>
              Adicionar aluno
            </h3>
          </div>

          <button
            type="button"
            onClick={
              onClose
            }
            disabled={
              saving
            }
          >
            ×
          </button>
        </div>


        <div className="aluno-modal-tabs">
          <button
            type="button"
            className={
              mode ===
              "existente"
                ? "active"
                : ""
            }
            onClick={() =>
              setMode(
                "existente",
              )
            }
          >
            Já cadastrado
          </button>

          <button
            type="button"
            className={
              mode ===
              "novo"
                ? "active"
                : ""
            }
            onClick={() =>
              setMode(
                "novo",
              )
            }
          >
            Novo aluno
          </button>
        </div>


        {mode ===
        "existente" ? (
          <>
            <input
              className="aluno-search"
              value={
                search
              }
              onChange={(
                event,
              ) =>
                setSearch(
                  event.target
                    .value,
                )
              }
              placeholder="Buscar por nome, telefone ou nível..."
            />


            {loading ? (
              <div className="turmas-empty">
                Carregando alunos...
              </div>
            ) : filteredAlunos.length ===
              0 ? (
              <div className="turmas-empty">
                <strong>
                  Nenhum aluno encontrado.
                </strong>

                <span>
                  Cadastre um novo aluno
                  pela outra aba.
                </span>
              </div>
            ) : (
              <div className="aluno-list">
                {filteredAlunos.map(
                  (aluno) => (
                    <button
                      key={
                        aluno.id
                      }
                      type="button"
                      className="aluno-list-item"
                      onClick={() =>
                        onAdicionarExistente(
                          aluno,
                        )
                      }
                      disabled={
                        saving
                      }
                    >
                      <div>
                        <strong>
                          {
                            aluno.nome
                          }
                        </strong>

                        <span>
                          Nível{" "}
                          {
                            aluno.nivel
                          }
                        </span>
                      </div>

                      <small>
                        {aluno.telefone ||
                          "Sem telefone"}
                      </small>
                    </button>
                  ),
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <label className="turma-field">
              Nome

              <input
                value={
                  form.nome
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      nome:
                        event.target
                          .value,
                    }),
                  )
                }
                placeholder="Nome do aluno"
              />
            </label>


            <label className="turma-field">
              Nível

              <select
                value={
                  form.nivel
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      nivel:
                        event.target
                          .value,
                    }),
                  )
                }
              >
                {LEVELS.map(
                  (level) => (
                    <option
                      key={
                        level
                      }
                      value={
                        level
                      }
                    >
                      {level}
                    </option>
                  ),
                )}
              </select>
            </label>


            <label className="turma-field">
              Telefone

              <input
                value={
                  form.telefone
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      telefone:
                        event.target
                          .value,
                    }),
                  )
                }
                placeholder="(99) 99999-9999"
              />
            </label>


            <label className="turma-field">
              Nascimento

              <input
                type="date"
                value={
                  form.nascimento
                }
                onChange={(
                  event,
                ) =>
                  setForm(
                    (current) => ({
                      ...current,

                      nascimento:
                        event.target
                          .value,
                    }),
                  )
                }
              />
            </label>


            <button
              type="button"
              className="turma-save-button aluno-create-button"
              onClick={
                handleCreate
              }
              disabled={
                saving ||
                !form.nome.trim()
              }
            >
              {saving
                ? "Cadastrando..."
                : "Cadastrar e adicionar"}
            </button>
          </>
        )}


        {error && (
          <div className="turmas-form-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}